// ---------------------------------------------------------------------------
// Quick pricing and offers - the till's and the editor's reads and writes.
// ---------------------------------------------------------------------------

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import { queryKeys } from '../lib/queryClient';
import type { ComboLifecycle, ComboOffer, ComboQuote } from '../types';

export const COMBOS_KEY = ['combos'] as const;
export const ACTIVE_COMBOS_KEY = ['combos', 'active'] as const;

/// What the virtual «العروض» tab sells right now. Polled gently so an offer
/// that expires or is disabled from the office leaves the till within a
/// minute without anybody reloading.
export function useActiveCombos(enabled = true) {
  return useQuery({
    queryKey: ACTIVE_COMBOS_KEY,
    queryFn: async () => (await api.get<{ combos: ComboOffer[] }>('/combos/active')).combos,
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

/// The reference value of a combo being built - asked of the server, which
/// prices it exactly as it will price the saved combo. Never worked out here.
export function useComboQuote() {
  return useMutation({
    mutationFn: (payload: { offerPriceLbp?: string | null; components: Array<{ productId: string; variantId?: string | null; quantity: string }> }) =>
      api.post<{ quote: ComboQuote }>('/combos/quote', payload),
  });
}

export function useCombos(lifecycle: 'all' | Lowercase<ComboLifecycle> = 'all', enabled = true) {
  return useQuery({
    queryKey: [...COMBOS_KEY, lifecycle],
    queryFn: async () => (await api.get<{ combos: ComboOffer[] }>(`/combos?lifecycle=${lifecycle}`)).combos,
    enabled,
    staleTime: 15_000,
  });
}

export interface ComboPayload {
  nameEn: string;
  nameAr: string;
  imageUrl?: string | null;
  offerPriceLbp: string;
  startsAt?: string | null;
  endsAt?: string | null;
  sortOrder?: number;
  components: Array<{ productId: string; variantId?: string | null; quantity: string }>;
}

function invalidateOffers(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: COMBOS_KEY });
}

export function useCreateCombo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ComboPayload & { state: 'DRAFT' | 'PUBLISHED' }) =>
      api.post<{ combo: ComboOffer; message: string }>('/combos', payload),
    onSuccess: () => invalidateOffers(queryClient),
  });
}

export function useUpdateCombo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: ComboPayload & { id: string; version: number; state?: 'DRAFT' | 'PUBLISHED' | 'DISABLED' }) =>
      api.put<{ combo: ComboOffer; message: string }>(`/combos/${id}`, payload),
    onSuccess: () => invalidateOffers(queryClient),
  });
}

export function useSetComboState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, state, version }: { id: string; state: 'DRAFT' | 'PUBLISHED' | 'DISABLED'; version: number }) =>
      api.post<{ combo: ComboOffer; message: string }>(`/combos/${id}/state`, { state, version }),
    onSuccess: () => invalidateOffers(queryClient),
  });
}

// ---------------------------------------------------------------------------
// Quick pricing
// ---------------------------------------------------------------------------

export interface QuickPriceResult {
  product: {
    id: string;
    priceVersion: number;
    sellingPriceLbp?: string | null;
    variant?: { id: string; sellingPriceLbp: string | null } | null;
  };
  message: string;
}

/// One price, saved the moment the field is left. Deliberately NOT invalidating
/// the product list on every keystroke-and-blur: the screen applies the
/// server's answer to its own row (the version, the confirmed figure), and the
/// catalogue is refreshed once when the user leaves. The POS grid's own
/// staleTime picks the change up on its next read.
export function useQuickPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      variantId,
      sellingPriceLbp,
      baseVersion,
    }: {
      productId: string;
      variantId?: string | null;
      sellingPriceLbp: string;
      baseVersion: number;
    }) =>
      api.patch<QuickPriceResult>(
        variantId
          ? `/products/${productId}/variants/${variantId}/quick-price`
          : `/products/${productId}/quick-price`,
        { sellingPriceLbp, baseVersion },
      ),
    onSuccess: () => {
      // The POS and product lists read from the same cache; mark them stale so
      // the next visit shows the new figure, without refetching this screen
      // under the user's hands.
      void queryClient.invalidateQueries({ queryKey: queryKeys.catalog.products(), refetchType: 'none' });
      void queryClient.invalidateQueries({ queryKey: COMBOS_KEY, refetchType: 'none' });
    },
  });
}

/// The whole sellable catalogue, read for ONE purpose: counting what is on
/// offer for the «العروض» chip whichever shelf is open. Same query the
/// products list uses, so it shares its cache.
export function useOffersCatalogue(enabled = true) {
  return useQuery({
    queryKey: queryKeys.catalog.products({ offers: 'count', pageSize: 200 }),
    queryFn: async () =>
      (await api.get<{ products: import('../types').Product[] }>('/products?pageSize=200&hasDiscount=true')).products,
    enabled,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
